import authTokenService from '@/services/auth/AuthTokenService';

interface GraphQLResponse<T = any> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{
      line: number;
      column: number;
    }>;
    path?: string[];
    extensions?: any;
  }>;
}

class LegacyGraphQLClient {
  private endpoint: string;
  
  constructor(endpoint: string = '/graphql') {
    this.endpoint = endpoint;
  }
  
  /**
   * Set the GraphQL endpoint URL
   */
  setEndpoint(endpoint: string): void {
    this.endpoint = endpoint;
  }
  
  /**
   * Execute a GraphQL query with the auth token
   */
  async query<T = any>(
    query: string, 
    variables: Record<string, any> = {}
  ): Promise<GraphQLResponse<T>> {
    return this.request<T>({
      query,
      variables,
    });
  }
  
  /**
   * Execute a GraphQL mutation with the auth token
   */
  async mutate<T = any>(
    mutation: string,
    variables: Record<string, any> = {}
  ): Promise<GraphQLResponse<T>> {
    return this.request<T>({
      query: mutation,
      variables,
    });
  }
  
  /**
   * Make the actual GraphQL request
   */
  private async request<T = any>(requestBody: {
    query: string;
    variables?: Record<string, any>;
  }): Promise<GraphQLResponse<T>> {
    try {
      const accessToken = authTokenService.getAccessToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      // Add auth token if available
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        throw new Error(`Network error: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('GraphQL request failed:', error);
      return {
        errors: [{
          message: error instanceof Error ? error.message : 'Unknown error occurred',
        }],
      };
    }
  }
}

// Create singleton instance
export const graphQLClient = new LegacyGraphQLClient();
export default graphQLClient; 